import { Bell } from "lucide-react";
import { useTranslate } from "ra-core";
import { useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useNotifications } from "./useNotifications";
// GAVION outlook-parity: one-click decision on a weak stop request
import { StopRequestActions } from "../email/stopRequests/StopRequestList";

import { stopRequestIdOf } from "./stopRequestLink";

const time = new Intl.DateTimeFormat("fr-CA", {
  timeZone: "America/Toronto",
  dateStyle: "short",
  timeStyle: "short",
});

// In-app relative paths only: no "//", backslash, control chars or schemes.
// eslint-disable-next-line no-control-regex
const SAFE_PATH = /^\/(?![/\\])[^\\\u0000-\u001f]*$/;

const safeLink = (link: string | null): string | null => {
  if (!link || !SAFE_PATH.test(link)) return null;
  try {
    const url = new URL(link, window.location.origin);
    if (url.origin !== window.location.origin) return null;
    return url.pathname + url.search + url.hash;
  } catch {
    return null;
  }
};

const formatTime = (iso: string): string | null => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : time.format(d);
};

export const NotificationsBell = () => {
  const translate = useTranslate();
  const navigate = useNavigate();
  const { items, unreadCount, markAllRead, markRead } = useNotifications();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          aria-label={translate("gavion.notifications.open", {
            smart_count: unreadCount,
          })}
        >
          <Bell className="size-5" />
          {unreadCount > 0 && (
            <span
              aria-hidden
              className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold leading-4 text-center"
            >
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel>
          {translate("gavion.notifications.title")}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {items.length === 0 && (
          <p className="px-2 py-3 text-sm text-muted-foreground">
            {translate("gavion.notifications.empty")}
          </p>
        )}
        {items.map((n) => (
          <div key={n.id}>
            <DropdownMenuItem
              onSelect={() => {
                markRead(n);
                const to = safeLink(n.link);
                if (to) navigate(to);
              }}
              className="flex flex-col items-start gap-0.5"
            >
              <span className={n.read_at ? "text-sm" : "text-sm font-semibold"}>
                {n.title}
              </span>
              {n.body && (
                <span className="text-xs text-muted-foreground">{n.body}</span>
              )}
              {formatTime(n.created_at) && (
                <span className="text-xs text-muted-foreground">
                  {formatTime(n.created_at)}
                </span>
              )}
            </DropdownMenuItem>
            {stopRequestIdOf(n.kind, n.link) !== null && !n.read_at ? (
              <div className="px-2 pb-2">
                <StopRequestActions
                  requestId={stopRequestIdOf(n.kind, n.link)!}
                  onDone={() => markRead(n)}
                />
              </div>
            ) : null}
          </div>
        ))}
        {unreadCount > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={markAllRead}>
              {translate("gavion.notifications.mark_all")}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
